/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import { ActionFail, ActionRequest, ActionSuccess, Asset, AssetUtils, Folder, XError } from '@wit/vtv-x-foundation-lib';
import { SagaIterator } from 'redux-saga';
import { all, call, put, takeLeading } from 'redux-saga/effects';
import { StoreState } from 'store/store';
import AssetInfoUtils from 'store/utils/assetInfoUtils';
import * as Context from './context';

export enum ActionType {
  INIT = 'app/folderInto/folder/INIT',
  SUCCESS = 'app/folderInto/folder/SUCCESS',
  ERROR = 'app/folderInto/folder/ERROR',
}

interface CurrentFolder {
  asset?: Folder;
  processed: boolean;
  processing: boolean;
  error?: XError;
}

type Init = ActionRequest<ActionType.INIT, Context.FolderInfoContext>;
type Success = ActionSuccess<ActionType.SUCCESS, Folder, Context.FolderInfoContext>;
type Error = ActionFail<ActionType.ERROR, Context.FolderInfoContext>;

type Actions = Init | Success | Error | Context.ClearContext;

type ReducerState = Record<string, CurrentFolder>;

const Actions = {
  init: (context: Context.FolderInfoContext): Init => ({ type: ActionType.INIT, meta: context }),
  success: (context: Context.FolderInfoContext, asset: Folder): Success => ({
    type: ActionType.SUCCESS,
    meta: context,
    payload: asset,
  }),
  error: (context: Context.FolderInfoContext, error: XError): Error => ({
    type: ActionType.ERROR,
    error,
    meta: context,
  }),
};

export const ActionCreators = {
  init: Actions.init,
};

const initialState: ReducerState = {};

export default function currentFolder(state = initialState, action: Actions): ReducerState {
  switch (action.type) {
    case ActionType.INIT: {
      const contextId = Context.getFolderContextId(action.meta);
      return {
        ...state,
        [contextId]: {
          processed: false,
          processing: true,
          error: undefined,
        },
      };
    }
    case ActionType.SUCCESS: {
      const contextId = Context.getFolderContextId(action.meta);
      return {
        ...state,
        [contextId]: {
          asset: action.payload,
          processed: true,
          processing: false,
          error: undefined,
        },
      };
    }
    case ActionType.ERROR: {
      const contextId = Context.getFolderContextId(action.meta);
      return {
        ...state,
        [contextId]: {
          processed: true,
          processing: false,
          error: action.error,
        },
      };
    }
    case Context.ActionType.CLEAR: {
      const contextId = Context.getFolderContextId(action.meta);

      const newState = { ...state };
      delete newState[contextId];

      return newState;
    }
    default:
      return state;
  }
}

export const Selectors = {
  getAsset: (state: StoreState, ctx: Context.FolderInfoContext) =>
    state.folderInfo.folder[Context.getFolderContextId(ctx)]?.asset,
  isProcessing: (state: StoreState, ctx: Context.FolderInfoContext) =>
    state.folderInfo.folder[Context.getFolderContextId(ctx)]?.processing === true,
  isProcessed: (state: StoreState, ctx: Context.FolderInfoContext) =>
    state.folderInfo.folder[Context.getFolderContextId(ctx)]?.processed === true,
  error: (state: StoreState, ctx: Context.FolderInfoContext) =>
    state.folderInfo.folder[Context.getFolderContextId(ctx)]?.error,
};

function* init(action: Init): SagaIterator {
  const context = action.meta;
  const { entryAssetId } = context;

  try {
    const asset: Asset | undefined = yield call(AssetInfoUtils.getMedia, entryAssetId);

    if (!asset || !AssetUtils.isFolder(asset)) {
      yield put(Actions.error(context, XError.fromError('content not found')));
      return;
    }

    yield put(Actions.success(context, asset));
  } catch (error) {
    yield put(Actions.error(context, XError.fromError(error)));
  }
}

export function* sagas() {
  yield all([takeLeading(ActionType.INIT, init)]);
}
